import React from 'react';
import { motion } from 'framer-motion';
import { metrics, categories } from '../data/metrics';
import { FiActivity, FiTrendingUp, FiTarget, FiAward } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import clsx from 'clsx';

const Dashboard = () => {
  const totalMetrics = metrics.length;
  const categoriesCount = categories.length;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-jeruel-blue to-jeruel-light rounded-3xl p-10 text-white shadow-2xl relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              Maintenance & Reliability Metrics
            </h1>
            <p className="text-xl text-white/90 max-w-3xl leading-relaxed">
              Comprehensive calculators and benchmarks based on industry best practices. Navigate using the sidebar to explore metrics by category.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold mb-1">{totalMetrics}</div>
                <div className="text-sm text-white/70">Total Metrics</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold mb-1">{categoriesCount}</div>
                <div className="text-sm text-white/70">Categories</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="flex items-center mb-1">
                  <SafeIcon icon={FiAward} className="text-2xl mr-2" />
                  <div className="text-2xl font-bold">Pro</div>
                </div>
                <div className="text-sm text-white/70">Certified</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="flex items-center mb-1">
                  <SafeIcon icon={FiTrendingUp} className="text-2xl mr-2" />
                  <div className="text-2xl font-bold">Live</div>
                </div>
                <div className="text-sm text-white/70">Calculations</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Start Guide */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Quick Start Guide</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-jeruel-grayblue"
          >
            <div className="w-12 h-12 bg-jeruel-soft rounded-xl flex items-center justify-center mb-4">
              <span className="text-2xl font-bold text-jeruel-blue">1</span>
            </div>
            <h3 className="font-bold text-lg text-slate-900 mb-2">Browse Categories</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Use the sidebar to explore metrics organized by the 5 pillars of maintenance excellence.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-jeruel-grayblue"
          >
            <div className="w-12 h-12 bg-jeruel-soft rounded-xl flex items-center justify-center mb-4">
              <span className="text-2xl font-bold text-jeruel-blue">2</span>
            </div>
            <h3 className="font-bold text-lg text-slate-900 mb-2">Select a Metric</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Click on any metric name to access its calculator, formula breakdown, and expert insights.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-jeruel-grayblue"
          >
            <div className="w-12 h-12 bg-jeruel-soft rounded-xl flex items-center justify-center mb-4">
              <span className="text-2xl font-bold text-jeruel-blue">3</span>
            </div>
            <h3 className="font-bold text-lg text-slate-900 mb-2">Calculate & Benchmark</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Input your data to calculate results and compare against industry benchmarks instantly.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Category Overview */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Metric Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, index) => {
            const catMetrics = metrics.filter((m) => m.categoryId === cat.id);
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={clsx(
                  "bg-white rounded-xl shadow-sm border p-6 hover:shadow-lg transition-all duration-300",
                  cat.border
                )}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={clsx("p-3 rounded-xl", cat.bg)}>
                    <SafeIcon name={cat.icon} className={clsx("text-2xl", cat.color)} />
                  </div>
                  <span className="text-2xl font-bold text-slate-300">{catMetrics.length}</span>
                </div>
                <h3 className="font-bold text-lg text-slate-900 mb-2">{cat.name}</h3>
                <p className="text-sm text-slate-600">
                  {catMetrics.length} metrics available for calculation and benchmarking
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Info Banner */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-12 bg-amber-50 border border-amber-200 rounded-xl p-6"
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-amber-100 rounded-lg">
            <SafeIcon icon={FiTarget} className="text-2xl text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-amber-900 mb-2">Industry Best Practices</h3>
            <p className="text-sm text-amber-800 leading-relaxed">
              All metrics, formulas, and benchmarks are based on industry-standard best practices for maintenance and reliability. Use the sidebar navigation to explore specific metrics within each category.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;